import {
  type Filters,
  MultiDropdownRenderItem,
  RuleDeletionAlert,
  RulesContent,
  RulesManagerFilters,
  useMultiDropdownFilter,
} from '@carboncopyco/editor-ui';
import { compareUrls } from '@carboncopyco/editor-utils';
import {
  type IDynamicEditData,
  IDynamicEditType,
  IEditActionsType,
} from '@carboncopyco/types';
import { MultiDropdownFilter } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { useMemo, useState } from 'react';
import { useEditorTools } from '../../context';
import { useEditor, useEditorMetadata } from '../../context/EditorSdkProvider';
import { SpotEditsManagerSection } from './SpotEditsManagerSection';
import { SpotEditsPreview } from './SpotEditsPreview';
import { useSpotEditsFilters } from './hooks/useSpotEditsFilters';
import { getFilteredEdits } from './utils';

const typeOptions = [
  { id: IDynamicEditType.text, label: 'Text' },
  { id: IDynamicEditType.chart, label: 'Chart' },
  { id: IDynamicEditType.image, label: 'Image' },
  { id: IDynamicEditType.list, label: 'List' },
  { id: IDynamicEditType.style, label: 'Style' },
  { id: IDynamicEditType.input, label: 'Input' },
  { id: IDynamicEditType.visibility, label: 'Visibility' },
  { id: IDynamicEditType.delete, label: 'Delete' },
  { id: IDynamicEditType.lock, label: 'Block' },
  { id: IDynamicEditType.unlock, label: 'Allow' },
];

const getDecodedUrlFromEdit = (edit: IDynamicEditData) => {
  const domain = new URL(edit.url).host;
  return edit.url.replace(domain, edit.domain);
};

export const SpotEditsManagerContent = ({
  spotEditsModifications,
}: {
  spotEditsModifications: IDynamicEditData[];
}) => {
  const modificationsWithDecodedUrl = useMemo(
    () =>
      spotEditsModifications.map((modification) => {
        return {
          ...modification,
          url: getDecodedUrlFromEdit(modification),
        };
      }),
    [spotEditsModifications]
  );
  const editor = useEditor();
  const { close: closeModal } = useEditorTools();
  const { location } = useEditorMetadata();
  const [showDeletionAlert, setShowDeletionAlert] = useBoolean();
  const [spotEditToDelete, setSpotEditToDelete] = useState<IDynamicEditData>();
  const pageUrl = location?.encodedUrl ?? '';

  const {
    selectedItems: typesFilterSelectedItems,
    onChange: onTypesFilterChange,
    onClear: onTypesFilterClear,
  } = useMultiDropdownFilter({
    fixedGroupItems: typeOptions,
  });

  const {
    searchValue,
    onSearchChange,
    selectedFilter,
    onFiltersChange,
    result: editsByFilters,
  } = useSpotEditsFilters({
    spotEditsModifications: modificationsWithDecodedUrl,
    pageUrl,
  });

  const filteredEdits = getFilteredEdits(
    editsByFilters,
    typesFilterSelectedItems
  );

  const handleOnClick = (edit: IDynamicEditData) => {
    const isCurrentPage = compareUrls(pageUrl, edit.url);
    closeModal('spotEditsManager');
    if (isCurrentPage) {
      editor.updateInspectAction(IEditActionsType.inspect);
      editor.navigateToTarget(edit);
      return;
    }
    editor.setNextEditorLoadedCallbacks([
      () => {
        editor.updateInspectAction(IEditActionsType.inspect);
        editor.navigateToTarget(edit);
      },
    ]);
    editor.updateUrl(edit.url);
  };

  return (
    <>
      <RulesContent>
        <RulesManagerFilters<Filters>
          onSearchChange={onSearchChange}
          onFiltersChange={onFiltersChange}
          dropdownItems={[
            { id: 'all', label: 'All pages' },
            { id: 'current', label: 'Current page' },
          ]}
          searchValue={searchValue}
          selectedFilter={selectedFilter}
        >
          <MultiDropdownFilter
            placeholder="Any type"
            fixedGroup={{
              items: typeOptions,
            }}
            renderItem={(props) => <MultiDropdownRenderItem {...props} />}
            selectedItems={typesFilterSelectedItems}
            onChange={onTypesFilterChange}
            onClear={onTypesFilterClear}
          />
        </RulesManagerFilters>

        {Object.entries(filteredEdits).map(([url, edits]) => {
          return (
            <SpotEditsManagerSection
              key={url}
              url={url}
              edits={edits}
              pageUrl={pageUrl}
              onMouseEnter={(edit) => editor.highlightTarget(edit)}
              onMouseLeave={(edit) => editor.destroyHighlightTarget(edit)}
              onClick={handleOnClick}
              onDelete={(edit) => {
                setSpotEditToDelete(edit);
                setShowDeletionAlert.setTrue();
              }}
            />
          );
        })}
      </RulesContent>
      <RuleDeletionAlert
        open={showDeletionAlert}
        onClose={setShowDeletionAlert.setFalse}
        onConfirm={async () => {
          await editor.deleteSpotEditsModifications([spotEditToDelete]);
          setShowDeletionAlert.setFalse();
        }}
        rulePreview={<SpotEditsPreview edit={spotEditToDelete} />}
      />
    </>
  );
};
